import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
  retainSearchParams,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { z } from "zod";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/lib/i18n";
import { SiteHeader } from "@/components/SiteHeader";
import { QaOverlay } from "@/components/QaOverlay";
import { I18nDebugPanel } from "@/components/I18nDebugPanel";
import { initRecoveryFlag } from "@/lib/recovery-flag";

// Runs at entry-chunk evaluation — before the Supabase client is lazily
// created and strips type=recovery from the URL hash. /auth/reset-password
// reads this flag to know a valid recovery link was followed.
initRecoveryFlag();

function NotFoundComponent() {
  // Root's notFoundComponent replaces RootComponent, so it renders OUTSIDE
  // LanguageProvider. Do not call useT() here — use static bilingual text.
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Stránka nenalezena / Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Požadovaná stránka neexistuje. / The requested page does not exist.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Domů / Home
          </Link>
        </div>
      </div>
    </div>
  );
}

// Stale bundle chunks from a previous deploy manifest as dynamic-import failures
// when the router tries to load a lazy route module. Detect that and force a
// hard reload (once per session, to avoid reload loops) so the browser fetches
// the current bundle instead of showing a generic error page.
function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const msg = String((error as { message?: string })?.message ?? error);
  const name = String((error as { name?: string })?.name ?? "");
  return (
    name === "ChunkLoadError" ||
    /Loading chunk [\w-]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg)
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  // Root's errorComponent replaces RootComponent, so it renders OUTSIDE
  // LanguageProvider. Do not call useT() here — use static bilingual text.
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
    if (isChunkLoadError(error) && typeof window !== "undefined") {
      try {
        const KEY = "lc_chunk_reload_at";
        const last = Number(sessionStorage.getItem(KEY) ?? 0);
        // Reload at most once per 60s to break any loop caused by a genuine bug.
        if (Date.now() - last > 60_000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
        }
      } catch {
        window.location.reload();
      }
    }
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Něco se pokazilo / Something went wrong
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Zkuste to prosím znovu. / Please try again.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              if (typeof window !== "undefined") {
                window.location.reload();
                return;
              }
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Zkusit znovu / Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Domů / Home
          </a>
        </div>
      </div>
    </div>
  );
}


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Keep the chosen UI language in the URL across every client-side <Link>
  // navigation so deep links (share/reload) always render in that language.
  validateSearch: z.object({ lang: z.string().optional() }).passthrough(),
  search: { middlewares: [retainSearchParams(["lang"])] },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1, viewport-fit=cover" },
      { name: "theme-color", content: "#fdfaf3" },
      { name: "color-scheme", content: "light" },
      { name: "application-name", content: "Cerveza.International" },
      { name: "apple-mobile-web-app-title", content: "Cerveza" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "default" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "format-detection", content: "telephone=no" },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { httpEquiv: "x-ua-compatible", content: "IE=edge" },
      { name: "msapplication-TileColor", content: "#fdfaf3" },
      { name: "msapplication-TileImage", content: "/favicon-192x192.png" },
      { name: "msapplication-config", content: "/browserconfig.xml" },
      { title: "Cerveza.International — Globální průvodce gastronomií" },
      { name: "description", content: "Mezinárodní katalog restaurací, barů, kaváren, čajoven, jídelen a stánků s občerstvením." },
      { property: "og:title", content: "Cerveza.International — Globální průvodce gastronomií" },
      { property: "og:description", content: "Mezinárodní katalog restaurací, barů, kaváren, čajoven, jídelen a stánků s občerstvením." },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Cerveza.International" },
      { property: "og:locale", content: "cs_CZ" },
      { property: "og:locale:alternate", content: "en_US" },
      { property: "og:locale:alternate", content: "es_ES" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Cerveza.International — Globální průvodce gastronomií" },
      { name: "twitter:description", content: "Mezinárodní katalog restaurací, barů, kaváren, čajoven, jídelen a stánků s občerstvením." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/007c6987-06ce-4758-95f1-32bc8ffc6ef5/id-preview-f96029e9--278bf456-cede-4cbd-9e65-ab96528f47cd.lovable.app-1781358957264.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Cerveza.International — mezinárodní gastronomický katalog" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/007c6987-06ce-4758-95f1-32bc8ffc6ef5/id-preview-f96029e9--278bf456-cede-4cbd-9e65-ab96528f47cd.lovable.app-1781358957264.png" },
      { name: "twitter:image:alt", content: "Cerveza.International — mezinárodní gastronomický katalog" },
    ],
    links: [
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "36x36", href: "/favicon-36x36.png" },
      { rel: "icon", type: "image/png", sizes: "48x48", href: "/favicon-48x48.png" },
      { rel: "icon", type: "image/png", sizes: "72x72", href: "/favicon-72x72.png" },
      { rel: "icon", type: "image/png", sizes: "96x96", href: "/favicon-96x96.png" },
      { rel: "icon", type: "image/png", sizes: "144x144", href: "/favicon-144x144.png" },
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/favicon-192x192.png" },
      { rel: "icon", type: "image/png", sizes: "256x256", href: "/favicon-256x256.png" },
      { rel: "icon", type: "image/png", sizes: "384x384", href: "/favicon-384x384.png" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/favicon-512x512.png" },
      // Apple touch icons — iPad, iPad Pro, iPhone Plus/Retina
      { rel: "apple-touch-icon", sizes: "120x120", href: "/apple-touch-icon-120x120.png" },
      { rel: "apple-touch-icon", sizes: "152x152", href: "/apple-touch-icon-152x152.png" },
      { rel: "apple-touch-icon", sizes: "167x167", href: "/apple-touch-icon-167x167.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon-180x180.png" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png" },
      // Maskable icons for Android / Chromebook launchers with adaptive shapes
      { rel: "icon", type: "image/png", sizes: "192x192", href: "/maskable-192.png", purpose: "maskable" } as unknown as Record<string, string>,
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/maskable-512.png", purpose: "maskable" } as unknown as Record<string, string>,
      { rel: "manifest", href: "/site.webmanifest" },

      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,600;0,700;0,800;1,600&display=swap" },
      // hreflang alternates for every supported UI language. Relative URLs
      // resolve against the current page, so this works for every route.
      // ?lang=xx is picked up by LanguageProvider on load.
      { rel: "alternate", hrefLang: "x-default", href: "?lang=en" },
      { rel: "alternate", hrefLang: "cs", href: "?lang=cs" },
      { rel: "alternate", hrefLang: "sk", href: "?lang=sk" },
      { rel: "alternate", hrefLang: "pl", href: "?lang=pl" },
      { rel: "alternate", hrefLang: "en", href: "?lang=en" },
      { rel: "alternate", hrefLang: "es", href: "?lang=es" },
      { rel: "alternate", hrefLang: "de", href: "?lang=de" },
      { rel: "alternate", hrefLang: "pt", href: "?lang=pt" },
      { rel: "alternate", hrefLang: "fr", href: "?lang=fr" },
      { rel: "alternate", hrefLang: "it", href: "?lang=it" },
      { rel: "alternate", hrefLang: "hr", href: "?lang=hr" },
      { rel: "alternate", hrefLang: "ro", href: "?lang=ro" },
      { rel: "alternate", hrefLang: "hu", href: "?lang=hu" },
      { rel: "alternate", hrefLang: "bg", href: "?lang=bg" },
    ],
  }),


  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}



function SkipLink() {
  return (
    <a
      href="#main-content"
      className="skip-link"
    >
      Přeskočit na obsah / Skip to content
    </a>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const router = useRouter();

  useEffect(() => {
    // Capture ?ref=CODE into localStorage on first visit so it survives sign-up.
    void import("@/lib/referral-tracker").then((m) => m.captureReferralFromUrl());

    // Zaznamenáme návštěvu z chatu nebo e-mailu (?src=chat / ?src=email), aby
    // se aktivace z těchto zdrojů objevila v adminu Doporučení i na mapě.
    void Promise.all([
      import("@/lib/visit-source"),
      import("@/lib/mapa-access-log.functions"),
      import("@/integrations/supabase/client"),
    ]).then(async ([{ currentVisitSource }, { logMapaAccess }, { supabase }]) => {
      const source = currentVisitSource();
      if (source === "app") return;
      const flag = `cz_visit_logged_${source}`;
      try {
        if (window.sessionStorage.getItem(flag)) return;
        window.sessionStorage.setItem(flag, "1");
      } catch { /* ignore */ }
      const { data } = await supabase.auth.getSession();
      void logMapaAccess({
        data: {
          result: data.session ? "allowed" : "anonymous",
          path: window.location.pathname,
          lang: null,
          userAgent: navigator.userAgent.slice(0, 500),
          email: data.session?.user?.email ?? null,
          userId: data.session?.user?.id ?? null,
          source,
        },
      }).catch(() => {});
    });


    // Attach the captured referral for ANY signed-in landing (incl. users coming
    // back from the e-mail confirmation link, which never passes through /auth).
    let unsubRef: (() => void) | undefined;
    void Promise.all([
      import("@/integrations/supabase/client"),
      import("@/lib/referral-attach"),
    ]).then(([{ supabase }, { attachStoredReferral }]) => {
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session) void attachStoredReferral();
      });
      const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
        if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && s?.user) {
          void attachStoredReferral();
        }
      });
      unsubRef = () => sub.subscription.unsubscribe();
    });

    // Refresh approximate map location whenever a signed-in user is present.
    // Fires on mount, on SIGNED_IN, and on every SPA navigation until the
    // server confirms coords are stored (server short-circuits within 24h).
    let unsubAuth: (() => void) | undefined;
    let unsubRouter: (() => void) | undefined;
    let done = false;
    void Promise.all([
      import("@/integrations/supabase/client"),
      import("@/lib/user-location.functions"),
    ]).then(([{ supabase }, { updateMyApproxLocation }]) => {
      const runOnce = async () => {
        if (done) return;
        try {
          const res = await updateMyApproxLocation();
          if (res?.ok) done = true;
        } catch {
          /* ignore; will retry on next navigation */
        }
      };
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session) void runOnce();
      });
      const { data } = supabase.auth.onAuthStateChange((event) => {
        if (event === "SIGNED_IN") {
          done = false;
          void runOnce();
        }
        if (event === "SIGNED_OUT") done = true;
      });
      unsubAuth = () => data.subscription.unsubscribe();

      // Retry on SPA route changes so users who signed in before a fix
      // (or under a datacenter without geo headers) get coords on next nav.
      unsubRouter = router.subscribe("onResolved", () => {
        void supabase.auth.getSession().then(({ data: s }) => {
          if (s.session) void runOnce();
        });
      });
    });

    // Track anonymous visitors (no session) exactly once per page-load, on any
    // route — pins on /mapa reflect activity across the whole site. On success
    // invalidate the public anonymous-pins feed so a freshly opened /mapa
    // (or one already open in another tab-like SPA state) picks up the row.
    let anonDone = false;
    void Promise.all([
      import("@/integrations/supabase/client"),
      import("@/lib/anon-visitor.functions"),
    ]).then(([{ supabase }, { trackAnonymousVisit }]) => {
      void supabase.auth.getSession().then(({ data }) => {
        if (data.session || anonDone) return;
        anonDone = true;
        void trackAnonymousVisit()
          .then((res) => {
            if (res?.ok) {
              console.info("[anon-visit] tracked", {
                source: (res as { source?: string }).source,
                country: (res as { country?: string | null }).country,
              });
              void queryClient.invalidateQueries({
                queryKey: ["public-anon-visitor-locations"],
              });
            } else {
              console.warn("[anon-visit] not tracked", res);
            }
          })
          .catch((err) => {
            console.warn("[anon-visit] failed", err);
          });
      });
    });


    return () => {
      unsubRef?.();
      unsubAuth?.();
      unsubRouter?.();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <SkipLink />
        <SiteHeader />
        <main id="main-content" tabIndex={-1}>
          {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
          <Outlet />
        </main>
        <Toaster richColors position="top-right" />
        <QaOverlay />
        <I18nDebugPanel />

      </LanguageProvider>
    </QueryClientProvider>
  );
}
